import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VoiceWave } from "@/components/Bubbles";
import { toast } from "sonner";
import { WakeSettings } from "@/components/WakeSettings";
import { buildWakeMatcher, getVoicePref, setVoicePref } from "@/lib/voice";
import { playUrl, stopAudio, ttsUrl, speakFallback, FATAL_SR_ERRORS } from "@/lib/audio";
import { Send, Sparkles, Boxes, Bell, Quote, Mic, Volume2, VolumeX, Infinity as InfinityIcon, Ear } from "lucide-react";

const SUGGESTIONS = [
  "Hey Omi, j'ai changé la pile du détecteur aujourd'hui, elle tient environ 3 ans.",
  "Mon frigo est-il encore garanti ?",
  "Quand dois-je faire la révision de ma voiture ?",
  "Qui a installé ma clim ?",
  "Hey Omi, donne-moi les numéros du Loto.",
];

export default function Assistant() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [audioOn, setAudioOn] = useState(true);
  const [continuous, setContinuous] = useState(false);
  const [wake, setWake] = useState(false);
  const [voicePref, setVoicePrefState] = useState(() => getVoicePref());
  useEffect(() => { setVoicePref(voicePref); }, [voicePref]);
  const audioOnRef = useRef(true);
  const continuousRef = useRef(false);
  const wakeRef = useRef(false);
  const wakeArmedRef = useRef(false);
  const wakeRecRef = useRef(null);
  const endRef = useRef(null);
  const recognitionRef = useRef(null);
  const speakQueueRef = useRef({ stop: false });
  const [searchParams] = useSearchParams();

  useEffect(() => { audioOnRef.current = audioOn; }, [audioOn]);
  useEffect(() => { continuousRef.current = continuous; }, [continuous]);
  useEffect(() => { wakeRef.current = wake; }, [wake]);

  const stripEmoji = (t) => t.replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE0F}\u{2B00}-\u{2BFF}👇]/gu, "").trim();

  const fallbackSpeak = (clean) => speakFallback(clean, {
    onstart: () => setSpeaking(true),
    onend: () => { setSpeaking(false); if (continuousRef.current) setTimeout(() => startListening(), 400); },
  });

  const speak = async (text) => {
    if (!audioOnRef.current) return;
    const clean = stripEmoji(text);
    if (!clean) return;
    const voice = getVoicePref();
    const mkUrl = (s) => ttsUrl(s, voice);
    // Découpe en courtes phrases pour démarrer l'audio quasi immédiatement (ressenti conversationnel).
    const parts = (clean.match(/[^.!?…]+[.!?…]*/g) || [clean]).map((s) => s.trim()).filter(Boolean);
    const chunks = [];
    let cur = "";
    for (const p of parts) {
      cur = cur ? `${cur} ${p}` : p;
      const threshold = chunks.length === 0 ? 24 : 70; // 1re phrase courte = audio quasi immédiat
      if (cur.length >= threshold) { chunks.push(cur); cur = ""; }
    }
    if (cur) chunks.push(cur);
    if (!chunks.length) chunks.push(clean);

    speakQueueRef.current.stop = true;
    const token = { stop: false };
    speakQueueRef.current = token;
    setSpeaking(true);
    try {
      for (let i = 0; i < chunks.length; i++) {
        if (token.stop) return;
        if (i + 1 < chunks.length) fetch(mkUrl(chunks[i + 1])).catch(() => {}); // pré-chauffe le cache de la phrase suivante
        await playUrl(mkUrl(chunks[i]));
      }
      if (!token.stop) { setSpeaking(false); if (continuousRef.current) setTimeout(() => startListening(), 350); }
    } catch (e) {
      if (!token.stop) fallbackSpeak(clean);
    }
  };

  const stopListening = () => { setListening(false); if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch (e) {} } };

  const startListening = () => {
    if (listening) { stopListening(); return; }
    speakQueueRef.current.stop = true;
    stopAudio();
    setSpeaking(false);
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("La reconnaissance vocale n'est pas supportée par ce navigateur"); return; }
    const rec = new SR();
    rec.lang = "fr-FR"; rec.interimResults = true; rec.continuous = false;
    recognitionRef.current = rec;
    let finalText = "";
    rec.onstart = () => { setListening(true); setInput(""); };
    rec.onresult = (e) => { let t = ""; for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript; finalText = t; setInput(t); };
    rec.onerror = (e) => {
      setListening(false);
      if (FATAL_SR_ERRORS.includes(e.error)) { setContinuous(false); continuousRef.current = false; toast.error("Micro inaccessible : autorise le micro pour ce site dans les réglages du navigateur."); }
    };
    rec.onend = () => { setListening(false); if (finalText.trim()) send(finalText.trim()); };
    try { rec.start(); } catch (e) { setListening(false); }
  };

  const greetAndListen = async () => {
    setContinuous(true); continuousRef.current = true;
    let greeting = `Hey ${user?.name || ""}, comment vas-tu aujourd'hui ? Que puis-je pour toi ?`.replace(/\s+/g, " ");
    try { const { data } = await api.get("/assistant/greeting"); if (data?.greeting) greeting = data.greeting; } catch (e) {}
    setMessages((m) => [...m, { role: "omy", text: greeting }]);
    speak(greeting);
    if (!audioOnRef.current) setTimeout(() => startListening(), 400);
  };

  const stopWake = () => {
    setWake(false); wakeRef.current = false; wakeArmedRef.current = false;
    if (wakeRecRef.current) { try { wakeRecRef.current.stop(); } catch (e) {} }
  };

  const startWake = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Reconnaissance vocale non supportée par ce navigateur"); return; }
    setWake(true); wakeRef.current = true; wakeArmedRef.current = true;
    stopAudio();
    const matcher = buildWakeMatcher();
    const rec = new SR();
    rec.lang = "fr-FR"; rec.continuous = true; rec.interimResults = true;
    wakeRecRef.current = rec;
    rec.onresult = (e) => {
      let t = ""; for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
      if (wakeArmedRef.current && matcher(t)) {
        wakeArmedRef.current = false;
        try { rec.stop(); } catch (er) {}
        greetAndListen();
      }
    };
    rec.onerror = (e) => {
      if (FATAL_SR_ERRORS.includes(e.error)) { stopWake(); toast.error("Micro inaccessible : autorise le micro pour activer « Hey Omy »."); }
    };
    rec.onend = () => {
      if (wakeRef.current && wakeArmedRef.current) setTimeout(() => { if (wakeRef.current && wakeArmedRef.current) { try { rec.start(); } catch (er) {} } }, 300);
    };
    try { rec.start(); } catch (er) { setWake(false); wakeRef.current = false; }
  };

  const toggleWake = () => { if (wakeRef.current) stopWake(); else startWake(); };

  useEffect(() => () => {
    stopWake();
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch (e) {} }
    speakQueueRef.current.stop = true;
    stopAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchParams.get("voice") === "1") { const t = setTimeout(() => greetAndListen(), 600); return () => clearTimeout(t); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/assistant/history").then((r) => {
      const hist = [];
      r.data.forEach((m) => {
        hist.push({ role: "user", text: m.user_message });
        hist.push({ role: "omy", text: m.reply, source: m.source });
      });
      setMessages(hist);
    });
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, {
        role: "omy", text: data.reply, source: data.source,
        created_items: data.created_items, created_reminders: data.created_reminders,
      }]);
      speak(data.reply);
      if (continuousRef.current && !audioOnRef.current) setTimeout(() => startListening(), 500);
      if (data.created_items?.length) toast.success(`${data.created_items.length} bien(s) mémorisé(s)`);
      if (data.created_reminders?.length) toast.success(`${data.created_reminders.length} rappel(s) créé(s)`);
    } catch (e) {
      setMessages((m) => [...m, { role: "omy", text: "OMY est momentanément indisponible. Réessaie." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100dvh-8rem)] lg:h-[calc(100dvh-4rem)]">
      <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-white/5">
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#E05A36,#F59E0B)" }}>
          <Sparkles size={16} className="text-white" />
        </div>
        <div>
          <h1 className="font-display font-semibold">Assistant OMY</h1>
          <p className="text-xs text-muted-foreground">Parle ou écris. OMY mémorise et te répond en vocal.</p>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <WakeSettings voice={voicePref} onVoice={setVoicePrefState} />
          <button data-testid="assistant-wake-toggle" title="Mot d'activation « Hey Omy »"
            onClick={toggleWake}
            className={`w-9 h-9 rounded-full flex items-center justify-center ${wake ? "text-primary bg-primary/15" : "text-muted-foreground hover:text-foreground"}`}>
            <Ear size={18} />
          </button>
          <button data-testid="assistant-continuous-toggle" title="Mode conversation continue"
            onClick={() => { const n = !continuous; setContinuous(n); if (!n) stopListening(); else startListening(); }}
            className={`w-9 h-9 rounded-full flex items-center justify-center ${continuous ? "text-primary bg-primary/15" : "text-muted-foreground hover:text-foreground"}`}>
            <InfinityIcon size={18} />
          </button>
          <button data-testid="assistant-audio-toggle" title="Voix d'OMY"
            onClick={() => { const n = !audioOn; setAudioOn(n); if (!n) { speakQueueRef.current.stop = true; stopAudio(); setSpeaking(false); } }}
            className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground">
            {audioOn ? <Volume2 size={18} className={speaking ? "text-primary" : ""} /> : <VolumeX size={18} />}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-6 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-muted-foreground mt-10">
            <p className="mb-6">« Hey Omi, qu'est-ce que je dois savoir aujourd'hui ? »</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-lg mx-auto">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} data-testid={`assistant-suggestion-${i}`} onClick={() => send(s)}
                  className="text-xs rounded-full border border-white/10 px-3 py-1.5 hover:border-primary/40 hover:text-foreground text-left">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`chat-msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
              m.role === "user" ? "bg-primary text-white rounded-br-sm" : "omy-glass rounded-bl-sm"}`}>
              <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
              {m.source && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground border-t border-white/10 pt-2">
                  <Quote size={12} /> Source : {m.source}
                </div>
              )}
              {m.created_items?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {m.created_items.map((it) => (
                    <span key={it.id} className="text-xs flex items-center gap-1 bg-black/30 rounded-full px-2 py-1">
                      <Boxes size={11} className="text-primary" /> {it.name}
                    </span>
                  ))}
                </div>
              )}
              {m.created_reminders?.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {m.created_reminders.map((r) => (
                    <span key={r.id} className="text-xs flex items-center gap-1 bg-black/30 rounded-full px-2 py-1">
                      <Bell size={11} className="text-accent" /> {r.title}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="omy-glass rounded-2xl rounded-bl-sm px-4 py-3"><VoiceWave bars={4} /></div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {wake && !listening && !speaking && (
        <div data-testid="assistant-wake-banner" className="flex items-center gap-3 mb-2 rounded-2xl bg-primary/10 border border-primary/20 px-4 py-2.5">
          <Ear size={16} className="text-primary shrink-0" />
          <span className="text-sm text-foreground/90">En attente de « Hey Omy »… dis-le pour me réveiller.</span>
        </div>
      )}
      {listening && (
        <div data-testid="assistant-listening" className="flex items-center gap-3 mb-2 rounded-2xl bg-red-500/10 border border-red-500/20 px-4 py-2.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse shrink-0" />
          <span className="text-sm text-foreground/90">OMY écoute…</span>
          <div className="ml-auto"><VoiceWave active bars={7} /></div>
        </div>
      )}
      <div className="omy-glass rounded-2xl p-2 flex items-center gap-2">
        <button data-testid="assistant-mic-btn" onClick={startListening} aria-label="Parler à OMY"
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all"
          style={{ background: listening ? "linear-gradient(135deg,#EF4444,#F97316)" : "linear-gradient(135deg,#E05A36,#F59E0B)" }}>
          <Mic size={18} className="text-white" />
        </button>
        <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Parle ou écris… « Hey Omi… »"
          className="flex-1 min-w-0 bg-transparent outline-none text-sm px-2 py-2" />
        {speaking && <VoiceWave active bars={4} />}
        <button data-testid="assistant-send-btn" onClick={() => send()} disabled={busy}
          className="w-9 h-9 rounded-full bg-primary flex items-center justify-center hover:bg-primary/90 disabled:opacity-50 shrink-0">
          <Send size={16} className="text-white" />
        </button>
      </div>
    </div>
  );
}
